<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" title="Introduction">
<div class="titlepage">
<div>
<div>
<h1 class="title" style="color: #80c4d0;"><a id="d0e111" xmlns:saxon="http://icl.com/saxon">Introduction</a></h1><a id="d0e111" xmlns:saxon="http://icl.com/saxon">
</a></div><a id="d0e111" xmlns:saxon="http://icl.com/saxon">
</a></div><a id="d0e111" xmlns:saxon="http://icl.com/saxon">
</a></div><a id="d0e111" xmlns:saxon="http://icl.com/saxon">
<p><span class="DropCap" style="color: #778f4f;">I</span> believe in traditions. I believe in rituals of hearth
                and home. I believe in chopping vegetables, wrapping presents, and setting the
                table. </p>
<p>Call me sentimental. </p>
<p>I miss the days when people got dressed to go on airplanes—or even just for dinner. I
                miss the time when families sat down to dine together; when people would talk and
                not just text; when kids would stay outside and ride their bikes until sundown; when
                our country was less fractured; when common courtesy was, well, common. </p>
<p>My mother, Marilyn, was a social worker. She taught me to send handwritten thank you
                notes and to make my bed each day. She taught me how to dress and to make a good
                first impression. She taught me that kindness is free and that a smile is always in
                style. </p>
<p>My father, Robert, was an entrepreneur; a semi-pro boxer, and a soldier in World War
                II. He taught me to take the road less traveled. He taught me to take care of my
                possessions. He taught me to live each day to the fullest, to always keep my car
                clean, and to approach life as a grand adventure. </p>
<p>I do my best to honor their legacy, and as I look at the work on these pages—my
                unfolding life’s work—it becomes evident that I am decidedly nostalgic. But I don’t
                feel that I am nostalgic for a certain architect, a certain form, or a certain
                chair. Rather, I am nostalgic for that simpler, more gracious time. I think that we
                as a society have a similar collective yearning. </p>
<p>Perhaps that is why midcentury modern design has had, and continues to have, a hold
                on the American consciousness. From television shows, to car commercials, to the
                fashion runway, what many critics have called a “trend” seems to have no indication
                of slowing down any time soon. </p>
<p>Yet not all of us can live in a pedigreed midcentury modern home. Fear not; the
                principles of midcentury modern design can be applied to the most unassuming of
                dwellings. The midcentury modernists were creating a movement that eschewed excess
                and needless ornament, so if your abode is humble, there is no reason to worry. I
                choose small and well-appointed over wanton grandeur any day. </p>
<p> This book is intended to help you create a midcentury <span class="emphasis">
<em>modern look</em>
</span> by offering foolproof tips for introducing modernist design into your home. </p>
<p>This book is also intended to help you make <span class="emphasis">
<em>midcentury </em>
</span>modern. In all of my interior designs, I strive to incorporate the principles
                of midcentury modernism in a way that feels fresh, relevant, and current. I never
                want my clients to feel as if they are living in a museum or time capsule. </p>
<p>Because I believe that nostalgia—and design, for that matter—is much more than skin
                deep, you will notice that much of my advice is dedicated to the life behind the
                style. </p>
<p>The celebrated midcentury designers were decidedly forward-thinking. They were using
                the new technology of the time (plastic, fiberglass, bent wood) and creating shapes
                that had never been seen before. They were dreamers and futurists. I like to think
                that if they were alive today, they would be creating “what’s new” and not resting
                on their sixty-year-old laurels. I feel that the best way to honor the legacy of
                these masters is to incorporate their pieces into designs that are decidedly of this
                century. </p>
<p>So, welcome, and enjoy. Design; create; dream. The past is our muse and the future
                our canvas. </p>
<div class="mediaobject" title="Photo of author's signature.">
<img alt="Photo of author's signature." src="images/CK_signature30082016.jpg"/>
</div>
<div class="mediaobject" title="Photo of outdoor dining area.">
<img alt="Photo of outdoor dining area." src="images/0147_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #24b5b7;"><a id="d0e111" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">1</p>
</a><h2><a id="d0e111" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e161" xmlns:saxon="http://icl.com/saxon">MAKE AN ENTRANCE</a></h2><a id="d0e161" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">We love our colorful doors in Palm Springs. It’s not uncommon to see orange
                    doors, pink doors, green doors. . . . Even if you don’t live in a modernist
                    post-and-beam home, why not have some fun with your first impression? (Don’t
                    forget, black or very dark brown can make a great statement too. Paired with
                    chrome or bronze, it will provide a bold and tailored first look.)</p>
<p class="cap">At our home in Palm Springs, we installed marble tile in a striking
                    black-and-white pattern, paired it with a vintage light found at a flea market,
                    and painted the door a high-gloss turquoise. Hello, gorgeous! </p>
</a></div><a id="d0e161" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of entrance to a home.">
<img alt="Photo of entrance to a home." src="images/0194_MMM.jpg"/>
<div class="caption">
<p> Photography © Grey Crawford.</p>
</div>
</div>
</a><div class="sidebar" style="background-color: #888171;"><a id="d0e161" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #fbd355;">2</p>
</a><h2 style="color: #80c4d0;"><a id="d0e161" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e178" xmlns:saxon="http://icl.com/saxon">DRESS THE PART</a></h2><a id="d0e178" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Call me old-fashioned, but I miss the days when people dressed up. When men wore
                    hats, women wore dresses, and everyone had a certain sense of decorum. I still
                    believe in dressing for the theater and for travel. I’m tired of getting on
                    airplanes alongside people wearing their pajamas. </p>
<p style="color: #ffffff;">It doesn’t need to be about vanity. For me, dressing the part is about putting
                    your best foot forward; it’s about feeling good about yourself and setting
                    positive intentions for your day. Dressing your best is not just a sign of
                    respect for yourself but also for the people around you. Boys, pull up your
                    pants! Girls, leave a little something to the imagination. And remember: good
                    clothes open all doors. </p>
</a></div><a id="d0e178" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a closet.">
<img alt="Photo of a closet." src="images/0195_MMM.jpg"/>
<div class="caption">
<p>Photography © Grey Crawford.</p>
</div>
</div>
</a><div class="sidebar" style="background-color: #4e8584;"><a id="d0e178" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">3</p>
</a><h2><a id="d0e178" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e195" xmlns:saxon="http://icl.com/saxon">MAKE YOUR BED</a></h2><a id="d0e195" xmlns:saxon="http://icl.com/saxon">
</a><h3 style="color: #ffffff;"><a id="d0e195" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e197" xmlns:saxon="http://icl.com/saxon">My secret to a beautiful and
                    easy-to-make bed </a></h3><a id="d0e197" xmlns:saxon="http://icl.com/saxon">
<p class="CKsecret" style="color: #ffffff;"><span class="BoldCaps">Step 1:</span> Pull the top sheet all the
                    way up. </p>
<p class="CKsecret" style="color: #ffffff;"><span class="BoldCaps">Step 2:</span> Pull the coverlet (I
                    prefer one in a fun or contrasting color) all the way up, hiding the wrinkly
                    sheets. </p>
<p class="CKsecret" style="color: #ffffff;"><span class="BoldCaps">Step 3:</span> Pull the duvet up, but
                    then turn it down in half or thirds at the foot of the bed. </p>
<p class="CKsecret" style="color: #ffffff;"><span class="BoldCaps">Step 4:</span> Stack the sleeping pillows
                    (David and I like two each) on top of the coverlet. </p>
<p class="CKsecret" style="color: #ffffff;"><span class="BoldCaps">Step 5:</span> Toss the decorative
                    pillow(s) in front of the sleeping pillows. I often opt for just one large
                    lumbar pillow. I never laden a bed with dozens of pillows that need to be thrown
                    off each night, then rearranged each morning—too much work!</p>
</a><h3 style="color: #ffffff;"><a id="d0e197" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e219" xmlns:saxon="http://icl.com/saxon">CK SECRET </a></h3><a id="d0e219" xmlns:saxon="http://icl.com/saxon">
<p class="CKsecret" style="color: #ffffff;">Don’t have much room for bedside tables? Free up surface space
                    by hanging your bedside lights. </p>
<p class="cap">I believe in making one’s bed every day and making it easy to do so.
                    This simple ritual makes me feel as if my life—or at least that small part of
                    it—is in order. What’s more, it is so much nicer to crawl into a made bed after
                    a long day. </p>
<p class="cap">In our busy modern world, I keep my bedding simple, striking, and foolproof. All
                    you need are sheets, pillows, coverlet, duvet, and one or two decorative
                    pillows.</p>
</a></div><a id="d0e219" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a bedroom.">
<img alt="Photo of a bedroom." src="images/0196_MMM.jpg"/>
<div class="caption">
<p>Photography © Grey Crawford.</p>
</div>
</div>
</a><div class="sidebar" style="background-color: #bdaa33;"><a id="d0e219" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #755e4d;">4</p>
</a><h2 style="color: #d1dadb;"><a id="d0e219" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e238" xmlns:saxon="http://icl.com/saxon">EAT BREAKFAST</a></h2><a id="d0e238" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Do not skip this important meal. It feeds your body <span class="emphasis">
<em>and</em>
</span> soul. Gather your thoughts, boost your metabolism, and set your daily
                    intentions before venturing out into the world.</p>
</a></div><a id="d0e238" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of author eating breakfast.">
<img alt="Photo of author eating breakfast." src="images/0009_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #665b4b;"><a id="d0e238" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ad9b84;">5</p>
</a><h2 style="color: #ffffff;"><a id="d0e238" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e253" xmlns:saxon="http://icl.com/saxon">LET PHOTOS TELL YOUR STORY </a></h2><a id="d0e253" xmlns:saxon="http://icl.com/saxon">
<p style="color: #c6ba5a;">As much as I love vintage celebrity photographs, or those by Slim Aarons—and I
                    do—lately I have been surrounding myself with family photographs. What better
                    way to channel the joie de vivre of your midcentury predecessors than by letting
                    them smile upon you? And don’t think you need to relegate the photos to a
                    hallway or dusty album. In my kitchen, I placed a photograph of my aunt (and
                    godmother), Audrey, on the open shelf where I keep my everyday china. Her beauty
                    and style inspire me daily. </p>
<p class="cap" style="color: #ffffff;">Isn’t my auntie beautiful? The Snoopy and Woodstock shakers are
                    relics from my childhood. Why keep such treasures packed away? They remind me of
                    where I come from.</p>
</a></div><a id="d0e253" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of kitchen.">
<img alt="Photo of kitchen." src="images/0006_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e1c445;"><a id="d0e253" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">6</p>
</a><h2><a id="d0e253" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e267" xmlns:saxon="http://icl.com/saxon">SPEAK ITALIAN</a></h2><a id="d0e267" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">When it comes to sofas—the workhorses of the room—I favor modern and new. Vintage
                    upholstery tends to be out of scale for our contemporary homes (and bodies!). </p>
<p style="color: #ffffff;">To keep things fresh and comfortable, my trick is to mix Italian modern
                    upholstery with vintage lamps, chairs, and tables for a blend that is
                    simultaneously now and totally timeless. </p>
<p class="cap" style="color: #d17d2c;">My colleague’s Boston Terrier, Dolly, strikes a pose at my home in
                    Palm Springs. It is not essential that your furry family members match your
                    décor, but it helps.</p>
</a></div><a id="d0e267" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0004_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/CK_Yosemite_Living_Room.jpg"/>
<div class="caption">
<p> Photography © Lance Gerber</p>
</div>
</div>
</a><div class="sidebar" style="background-color: #b4bfb9;"><a id="d0e267" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #fad57e;">7</p>
</a><h2 style="color: #4c514a;"><a id="d0e267" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e291" xmlns:saxon="http://icl.com/saxon">BRASS IS BACK</a></h2><a id="d0e291" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">For some of us, it never left. Few other metals allow for the detail, warmth, and
                    patina the way that brass does. I’m not a purist when it comes to metals—mix and
                    match throughout your home! </p>
<p class="cap" style="color: #fad57e;">Caveat, modernista: I tend to keep my door and plumbing hardware
                    consistent throughout the home, but then get adventurous when it comes to
                    lighting and accessories.</p>
</a></div><a id="d0e291" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of glass and brass table.">
<img alt="Photo of glass and brass table." src="images/0025_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #626060;"><a id="d0e291" xmlns:saxon="http://icl.com/saxon">
<p class="number">8</p>
</a><h2 style="color: #edc256;"><a id="d0e291" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e305" xmlns:saxon="http://icl.com/saxon">Filter Your View</a></h2><a id="d0e305" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">As modern floor plans became more open, thanks to post-and-beam construction,
                    architects got creative with room dividers. Many a midcentury manse featured a
                    carved wood screen, functionally dividing the space while allowing light and
                    breezes to pass through. </p>
<p class="cap" style="color: #ffffff;">At my home in Palm Springs, I used a Don Harvey brise-soleil (French
                    for “break the sun”) salvaged from the exterior of a medical building in
                    Boulder, Colorado, to separate my dining and living rooms. </p>
</a></div><a id="d0e305" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a room divider.">
<img alt="Photo of a room divider." src="images/0023_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #671c19;"><a id="d0e305" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c43d28;">9</p>
</a><h2 style="color: #daf8ba;"><a id="d0e305" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e319" xmlns:saxon="http://icl.com/saxon">A FINE BALANCE</a></h2><a id="d0e319" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">One of the most recognizable designs of the era, tension lamps were created to go
                    from the floor to a standard eight-foot ceiling. They still work in most
                    contemporary homes, drawing the eye upward and illuminating dark corners with
                    nostalgic flair. It is time to bring this mid-mod classic out of retirement! </p>
<p class="cap" style="color: #daf8ba;">I picked up this handsome lamp for less than $100 at a thrift store;
                    it strikes an instant mid-mod note in my otherwise modern den.</p>
</a></div><a id="d0e319" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a recyled lamp.">
<img alt="Photo of a recyled lamp." src="images/0013_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #000000;"><a id="d0e319" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #f5bc23;">10</p>
</a><h2 style="color: #e57528;"><a id="d0e319" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e333" xmlns:saxon="http://icl.com/saxon">BUTT OUT</a></h2><a id="d0e333" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">I have never smoked a cigarette in my life, but I collect vintage ashtrays. The
                    artistry is often incredible, so I use them to gather my personal effects. It
                    lets me feel as cool as Don Draper, with none of the nasty side effects.</p>
</a></div><a id="d0e333" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of ashtrays.">
<img alt="Photo of ashtrays." src="images/0018_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of ashtrays.">
<img alt="Photo of ashtrays." src="images/page21.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #f5bc23;"><a id="d0e333" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #f8d883;">11</p>
</a><h2><a id="d0e333" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e350" xmlns:saxon="http://icl.com/saxon">Go for the Gold </a></h2><a id="d0e350" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Silver, chrome, and brushed nickel are all the rage in today’s contemporary
                    interiors. Why not add some vintage glamour with the king of all metals? </p>
</a></div><a id="d0e350" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of gold wall.">
<img alt="Photo of gold wall." src="images/0019_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #40582a;"><a id="d0e350" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #4b8ba;">12</p>
</a><h2 style="color: #e16026;"><a id="d0e350" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e362" xmlns:saxon="http://icl.com/saxon">go postal</a></h2><a id="d0e362" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">A midcentury mindset can start curbside. Tell the world you appreciate the days
                    of snail mail, landlines, and casseroles by swapping out your mundane mailbox
                    for this stylish one from Modbox. </p>
</a></div><a id="d0e362" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a midcentury mailbox.">
<img alt="Photo of a midcentury mailbox." src="images/0216_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #f8cd23;"><a id="d0e362" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #1eb3b1;">13</p>
</a><h2 style="color: #e9882a;"><a id="d0e362" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e374" xmlns:saxon="http://icl.com/saxon">LAMP UP</a></h2><a id="d0e374" xmlns:saxon="http://icl.com/saxon">
<p>Vintage lamps are undoubtedly my favorite item to shop for. A good vintage lamp
                    will instantly change the feel of the most ho-hum room. </p>
</a><h3 style="color: #d82327;"><a id="d0e374" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e378" xmlns:saxon="http://icl.com/saxon">CK SECRET </a></h3><a id="d0e378" xmlns:saxon="http://icl.com/saxon">
<p class="CKsecret" style="color: #d82327;">Online websites like Etsy and Chairish are treasure troves of
                    single vintage lamps, many of which cost less than $100.</p>
</a></div><a id="d0e378" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of vintage lamp.">
<img alt="Photo of vintage lamp." src="images/0002_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e16026;"><a id="d0e378" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #d5ce2c;">14</p>
</a><h2 style="color: #ffffff;"><a id="d0e378" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e390" xmlns:saxon="http://icl.com/saxon">ORANGE YOU GLAD?</a></h2><a id="d0e390" xmlns:saxon="http://icl.com/saxon">
<p>Few colors pack the punch of orange. It is the caffeine of the color wheel. A
                    small (or large) dose of the hue is sure to put a smile on your face and pep in
                    your step. </p>
</a></div><a id="d0e390" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of living area with orange wall.">
<img alt="Photo of living area with orange wall." src="images/0034_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #979595;"><a id="d0e390" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">15</p>
</a><h2 style="color: #ad3b6a;"><a id="d0e390" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e402" xmlns:saxon="http://icl.com/saxon">BE BOLD</a></h2><a id="d0e402" xmlns:saxon="http://icl.com/saxon">
<p>It is no secret that I like things crisp, graphic, and bright. Modern artwork is
                    my preferred method to place a midcentury-inspired interior decidedly in this
                    century. A punch of geometric pow paired with vintage furnishings just stirs my
                    soul. Go bold or go home! </p>
</a></div><a id="d0e402" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of modern art.">
<img alt="Photo of modern art." src="images/0033_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #000000;"><a id="d0e402" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">16</p>
</a><h2 style="color: #80c4d0;"><a id="d0e402" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e414" xmlns:saxon="http://icl.com/saxon">CHANNEL YOUR INNER LUCY AND
                    RICKY</a></h2><a id="d0e414" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Two beds were <span class="bold">
<strong>de rigueur </strong>
</span>for midcentury married couples, or at least those on TV! Who can forget
                    Lucy and Ricky Ricardo, or June and Ward Cleaver, in their separate beds? </p>
<p style="color: #ffffff;">Many of the homes I design incorporate at least one guest room with twin beds.
                    It’s a practical way to accommodate family and friends, and the symmetry looks
                    quite smashing.</p>
<p style="color: #ffffff;">In this guest room, I created one long wall-size headboard. While the room is
                    often used for children, when a couple comes to stay, the mattresses are easily
                    pushed together and topped with a featherbed. Snuggle away!</p>
</a></div><a id="d0e414" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a bedroom.">
<img alt="Photo of a bedroom." src="images/0032_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #c8c6c6;"><a id="d0e414" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">17</p>
</a><h2><a id="d0e414" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e433" xmlns:saxon="http://icl.com/saxon">(Neutra)Face It</a></h2><a id="d0e433" xmlns:saxon="http://icl.com/saxon">
<p>I have this thing with fonts. It is a bit of a problem. Typeface sets the tone,
                    so make your home address ring modern with the king of all lettering:
                    Neutraface. Created by the architect Richard Neutra, his numerals are available
                    online and will instantly enhance the curb appeal of the most humble abode. </p>
</a></div><a id="d0e433" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of exterior of home.">
<img alt="Photo of exterior of home." src="images/0204_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #f0ecec;"><a id="d0e433" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #f5bc23;">18</p>
</a><h2 style="color: #e57528;"><a id="d0e433" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e445" xmlns:saxon="http://icl.com/saxon">EMBRACE THE OLD AND THE
                    NEW</a></h2><a id="d0e445" xmlns:saxon="http://icl.com/saxon">
<p>The midcentury masters were pushing the envelope of technology and innovation. In
                    a few short decades, we now have more technology in the palm of our hand than
                    they could have dreamt about. In this modern age, why not surround yourself with
                    some well-chosen midcentury nostalgia? It is a wonderful reminder of where we
                    have come from and a nudge at where we have yet to go. So dream on, modernista!
                    The world is literally at your fingertips. </p>
</a></div><a id="d0e445" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of vintage posters.">
<img alt="Photo of vintage posters." src="images/0045_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #bd3128;"><a id="d0e445" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #549091;">19</p>
</a><h2 style="color: #fbd355;"><a id="d0e445" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e457" xmlns:saxon="http://icl.com/saxon">BURN BRIGHT </a></h2><a id="d0e457" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">I’ve seen maudlin brick or stone fireplaces in many homes from a variety of
                    periods, including my parents’ 1970s rancher. A coat of white paint is an
                    affordable and foolproof trick to add midcentury appeal to any hearth.</p>
<p class="cap" style="color: #ffffff;">Here, the crushed glass adds a dash of millenium modernity to an
                    otherwise vintage vibe.</p>
</a></div><a id="d0e457" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of white fireplace.">
<img alt="Photo of white fireplace." src="images/0035_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e9e8e4;"><a id="d0e457" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c43d28;">20</p>
</a><h2><a id="d0e457" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e471" xmlns:saxon="http://icl.com/saxon">STAR GAZE</a></h2><a id="d0e471" xmlns:saxon="http://icl.com/saxon">
<p>I like to avoid cliché, but I am all about icons. The “Sputnik” chandelier—named
                    after the Soviet Union’s 1957 satellite, which launched the space race and the
                    “atomic age” of design—always casts a heavenly glow. </p>
</a></div><a id="d0e471" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of Sputnik chandelier.">
<img alt="Photo of Sputnik chandelier." src="images/0044_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e5e1e1;"><a id="d0e471" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #edc256;">21</p>
</a><h2><a id="d0e471" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e483" xmlns:saxon="http://icl.com/saxon">PATTERN PLAY</a></h2><a id="d0e483" xmlns:saxon="http://icl.com/saxon">
<p>Pile on the pattern! Mix some modern geometry with vintage fabrics for a look
                    that is at once retro-chic and of-the-moment. In this case, more really is
                    more.</p>
</a></div><a id="d0e483" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of patterns in a bedroom.">
<img alt="Photo of patterns in a bedroom." src="images/0042_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #a4a738;"><a id="d0e483" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #f6c849;">22</p>
</a><h2 style="color: #8a4f26;"><a id="d0e483" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e495" xmlns:saxon="http://icl.com/saxon">LIFE IS BEAUTIFUL</a></h2><a id="d0e495" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">For this client’s home, we tracked down <span class="emphasis">
<em>Life</em>
</span> magazines from the year the house was built. No matter the age of your
                    home, vintage magazines will take you back to a simpler, more gracious time. </p>
<p class="cap">Have a new or new-ish house? Consider buying vintage magazines
                    (easily found online) from the year you were born—or the year you wish you
                    were.</p>
</a></div><a id="d0e495" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of display of vintage magazines.">
<img alt="Photo of display of vintage magazines." src="images/0029_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #a2bba6;"><a id="d0e495" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ae8430;">23</p>
</a><h2 style="color: #e26427;"><a id="d0e495" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e512" xmlns:saxon="http://icl.com/saxon">CAN IT</a></h2><a id="d0e512" xmlns:saxon="http://icl.com/saxon">
<p>Add instant flair to your kitchen with a set of vintage canisters. I happen to
                    use my mother’s. Don’t do much baking? Get creative! They are equally suited to
                    storing packets of sweetener, straws, protein powder, and K-Cups® (in my
                    kitchen, anyway!). </p>
</a></div><a id="d0e512" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of vintage canisters.">
<img alt="Photo of vintage canisters." src="images/0028_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #6b6370;"><a id="d0e512" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #fbd355;">24</p>
</a><h2 style="color: #80c4d0;"><a id="d0e512" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e524" xmlns:saxon="http://icl.com/saxon">Shady Deals</a></h2><a id="d0e524" xmlns:saxon="http://icl.com/saxon">
<p>Midcentury masters were all about the details. They left no design stone
                    unturned, including one that is often overlooked—the humble lampshade. Today’s
                    boring white diffusers just lack the luster of their predecessors, which often
                    featured tassels, decorative tape trim, or bold colors. </p>
<p class="cap">Get creative, dear modernist! Swap out a standard shade for a deep
                    color. Buy some tape trim, break out the glue gun, and let there be light.</p>
</a></div><a id="d0e524" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of exotic lamp.">
<img alt="Photo of exotic lamp." src="images/0037_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #40582a;"><a id="d0e524" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #a6dbec;">25</p>
</a><h2 style="color: #eec677;"><a id="d0e524" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e538" xmlns:saxon="http://icl.com/saxon">GO EXOTIC</a></h2><a id="d0e538" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">With the advent of commercial air travel, a certain wanderlust took hold of many
                    midcentury Americans. As they ventured to far flung locales, or dreamt of doing
                    so, all sorts of exotica made its way into American interior design. Even if you
                    won’t be visiting Palm Springs anytime soon, incorporating tropical décor into
                    your home can transform your bedroom into your favorite hotel suite.</p>
<p class="cap" style="color: #ffffff;">The Beverly Hills Hotel and Blanche Devereaux’s bedroom have nothing
                    on this wallpaper from my friends at Phillip Jeffries.</p>
</a></div><a id="d0e538" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bedroom.">
<img alt="Photo of bedroom." src="images/0041_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #778f4f;"><a id="d0e538" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #134949;">26</p>
</a><h2 style="color: #ffffff;"><a id="d0e538" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e552" xmlns:saxon="http://icl.com/saxon">Get Skin in the Game</a></h2><a id="d0e552" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Cowhide rugs have been a staple of modern decorating since before the midcentury
                    era. Eileen Gray, designer of her eponymous chrome and glass circular side
                    table, famously decorated her apartment with them back in the 1930s. I like to
                    use cowhide rugs in spaces that are difficult to fit a standard rectangular area
                    rug, such as this entryway.</p>
</a></div><a id="d0e552" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of an entryway.">
<img alt="Photo of an entryway." src="images/page44.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #eaf1e7;"><a id="d0e552" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #a1873b;">27</p>
</a><h2 style="color: #778f4f;"><a id="d0e552" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e564" xmlns:saxon="http://icl.com/saxon">Make a Wish </a></h2><a id="d0e564" xmlns:saxon="http://icl.com/saxon">
<p>Danish designer Hans Wegner, an icon of “organic functionality,” designed more
                    than five hundred chairs in his lifetime, but few remain as relevant today as
                    his 1949 Wishbone Chair. I paired Wegner’s chairs with a traditional trestle
                    table and Jonathan Adler chandelier for a timeless look. </p>
</a></div><a id="d0e564" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of dining area.">
<img alt="Photo of dining area." src="images/0071_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #1a838d;"><a id="d0e564" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #8ba445;">28</p>
</a><h2 style="color: #edc256;"><a id="d0e564" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e576" xmlns:saxon="http://icl.com/saxon">Let Nature Be Your Guide </a></h2><a id="d0e576" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Midcentury modern architects were using new technologies and building techniques
                    to enhance our connection to the outside environment. If you don’t live in a
                    post-and-beam modern masterpiece, never fear! You can still bring the outdoors
                    in with a nature-inspired color palette. Blues and greens are my go-to
                    decorating colors. With just a dash of sunny yellow, I can practically smell the
                    fresh air and feel the sun on my face. </p>
</a></div><a id="d0e576" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0077_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0076_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #254d27;"><a id="d0e576" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #bdd453;">29</p>
</a><h2 style="color: #00a3db;"><a id="d0e576" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e593" xmlns:saxon="http://icl.com/saxon">Get Shipwrecked</a></h2><a id="d0e593" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">I grew up watching <span class="emphasis">
<em>Gilligan’s Island</em>
</span> and <span class="emphasis">
<em>The Love Boat</em>
</span>, and I have sought warm climes ever since. (There is a reason I found my
                    way to Palm Springs!) The idea of getting shipwrecked and spending the rest of
                    one’s days on sunny shores has a certain hold on the American psyche. The
                    popularity of the Tiki style and restaurants like Trader Vic’s is a testament to
                    this wanderlust. We may not be able to live on Fantasy Island, but a few hours
                    in an easy-to-install hammock will put you in a tropical state of mind. </p>
</a></div><a id="d0e593" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of hanging hammock.">
<img alt="Photo of hanging hammock." src="images/0074_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #ccd7d4;"><a id="d0e593" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #8ec355;">30 </p>
</a><h2 style="color: #40b8ba;"><a id="d0e593" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e611" xmlns:saxon="http://icl.com/saxon">Lighten Up</a></h2><a id="d0e611" xmlns:saxon="http://icl.com/saxon">
<p>In many ways, modernism was a backlash against excessive ornamentation and
                    overwrought copycat architecture. Modernists were wiping the slate clean. You
                    can do the same by keeping things simple—a crisp white color palette will always
                    give a fresh outlook on life. </p>
<p class="cap" style="color: #40b8ba;">Against a bright backdrop, small pops of color make a big
                    statement.</p>
</a></div><a id="d0e611" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bathroom.">
<img alt="Photo of bathroom." src="images/0068_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #ab264d;"><a id="d0e611" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">31 </p>
</a><h2 style="color: #baaa67;"><a id="d0e611" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e625" xmlns:saxon="http://icl.com/saxon">Metal-Urge</a></h2><a id="d0e625" xmlns:saxon="http://icl.com/saxon">
<p>As midcentury designers experimented with new forms, no material was off limits:
                    wood, fiberglass, acrylic, and metal were all just putty in their hands. </p>
</a></div><a id="d0e625" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of metal chest of drawers.">
<img alt="Photo of metal chest of drawers." src="images/0066_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #fbd355;"><a id="d0e625" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #b87a2a;">32 </p>
</a><h2 style="color: #778f4f;"><a id="d0e625" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e637" xmlns:saxon="http://icl.com/saxon">Let the Sunshine In</a></h2><a id="d0e637" xmlns:saxon="http://icl.com/saxon">
<p>You do not need to live in Palm Springs to hail the sunshine! A common midcentury
                    decorating theme, these sunbursts came in a variety of forms—wood, metal, and
                    more. A nice counterpoint to canvas artwork, a sunburst is sure to brighten the
                    coldest of winters and add a dash of midcentury optimism to your décor. </p>
</a></div><a id="d0e637" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of sunburst.">
<img alt="Photo of sunburst." src="images/0075_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #c6bc8e;"><a id="d0e637" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #e2dcb1;">33 </p>
</a><h2 style="color: #258941;"><a id="d0e637" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e649" xmlns:saxon="http://icl.com/saxon">Clay-mation</a></h2><a id="d0e649" xmlns:saxon="http://icl.com/saxon">
<p style="color: #5e3b1d;">A close runner-up to glass bottles, midcentury pottery is my second favorite
                    obsession to shop for. They are a bit harder to find but well worth the effort.
                    Group them together for maximum impact. And don’t be afraid to put them to use!
                    Add flowers, plants, fruit, whatever you can dream up. Our grandparents valued
                    the utility of everyday objects, not just the beauty.</p>
</a></div><a id="d0e649" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of pottery.">
<img alt="Photo of pottery." src="images/0078_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #135496;"><a id="d0e649" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #1ba6c1;">34 </p>
</a><h2 style="color: #d0ecf0;"><a id="d0e649" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e661" xmlns:saxon="http://icl.com/saxon">Color Me Blue</a></h2><a id="d0e661" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">True story: Years ago, when my design firm had only been open a short while, the
                    home editor for the <span class="emphasis">
<em>Los Angeles Times</em>
</span> came to Palm Springs looking for projects. I showed her a few homes I
                    had done, and I was over the moon—but a bit incredulous—a few weeks later when
                    she called and said, “I want to publish that blue house.” </p>
<p style="color: #ffffff;">I said, “I don’t like blue. What do you mean?” </p>
<p style="color: #ffffff;">Her kind response, “Christopher, apparently you do.” </p>
<p style="color: #ffffff;">She was correct, of course; nearly every shade of the watery hue was represented
                    in that particular project. To this day, blue is one of my tried-and-true
                    colors. If you want to add a dash of midcentury cool to your home, look no
                    further than blue and its sassy cousins aqua and turquoise. </p>
</a></div><a id="d0e661" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of blue bathroom.">
<img alt="Photo of blue bathroom." src="images/0080_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #d5b62d;"><a id="d0e661" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">35 </p>
</a><h2 style="color: #40b8ba;"><a id="d0e661" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e682" xmlns:saxon="http://icl.com/saxon">Take It Outside</a></h2><a id="d0e682" xmlns:saxon="http://icl.com/saxon">
<p style="color: #4c514a;">A primary tenet of midcentury architecture was to create a connection to the
                    environment. One of the first and greatest modern houses, Philip Johnson’s Glass
                    House (1949), was a glass box dropped into the Connecticut landscape. Given the
                    climate, the connection was largely visual. When the principles of modernism
                    moved west to warmer temperatures, the great architects worked to make those
                    glass walls movable, to create a physical connection to the environment. </p>
<p class="cap" style="color: #ffffff;">No matter where you live, even if only for a month or two in the
                    summer, take it outside! Create an outdoor living room with rugs, pillows,
                    artwork, and all the creature comforts of your indoor rooms. You will find that
                    you create some of your happiest memories in the <span class="emphasis">
<em>plein air</em>
</span>.</p>
</a></div><a id="d0e682" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of outdoor seating area.">
<img alt="Photo of outdoor seating area." src="images/0087_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #b9d243;"><a id="d0e682" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #fbd355;">36</p>
</a><h2 style="color: #034e29;"><a id="d0e682" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e699" xmlns:saxon="http://icl.com/saxon">It <span class="emphasis">
<em>Is</em>
</span> Easy Being Green</a></h2><a id="d0e699" xmlns:saxon="http://icl.com/saxon">
<p>The optimism of Kermit the Frog, the magic of chlorophyll, the aroma of fresh-cut
                    grass—green’s got it all going on. </p>
<p class="cap" style="color: #ffffff;">My Giselle chairs flank a vintage sideboard adorned with an eclectic
                    mix of decorative objects.</p>
</a></div><a id="d0e699" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of green room.">
<img alt="Photo of green room." src="images/0079_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #d3cfcf;"><a id="d0e699" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c43d28;">37</p>
</a><h2 style="color: #a1873b;"><a id="d0e699" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e716" xmlns:saxon="http://icl.com/saxon">African Rhythm</a></h2><a id="d0e716" xmlns:saxon="http://icl.com/saxon">
<p>March to the beat of a different drum. Mask your insecurities. Walk on the wild
                    side. Dream of far-flung locales and wild safaris. A dash of Africana in your
                    home can take you places you never thought you could go. </p>
</a></div><a id="d0e716" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of African masks.">
<img alt="Photo of African masks." src="images/0090_MMM_fixed.jpg"/>
</div>
<div class="mediaobject" title="Photo of African masks.">
<img alt="Photo of African masks." src="images/page67.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #7ea2a1;"><a id="d0e716" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #dd993f;">38</p>
</a><h2 style="color: #ffffff;"><a id="d0e716" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e733" xmlns:saxon="http://icl.com/saxon">Throw a Curve Ball</a></h2><a id="d0e733" xmlns:saxon="http://icl.com/saxon">
<p>Every yin needs a yang. Hot needs cold. Darkness cannot exist without light.
                    While midcentury design is known for its clean, straight lines, they are only
                    enhanced by the unexpected addition of a sinuous curve. </p>
<p class="cap">Architect Frank Gehry’s iconic Wiggle Chair (1972) is a perfect
                    complement to a metal desk and Phillip Jeffries grasscloth.</p>
</a></div><a id="d0e733" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of Wiggle Chair.">
<img alt="Photo of Wiggle Chair." src="images/0088_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #29ad79;"><a id="d0e733" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #edc256;">39</p>
</a><h2 style="color: #ffffff;"><a id="d0e733" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e747" xmlns:saxon="http://icl.com/saxon">Send a Handwritten Note</a></h2><a id="d0e747" xmlns:saxon="http://icl.com/saxon">
<p>My mother raised me to write thank-you notes. I never enjoyed the chore, but I
                    dutifully wrote them after each birthday and holiday. In an age of electronic
                    communication, paper-and-ink stands out. I can honestly say that handwritten
                    notes are one of the secrets to my success. Thanks, Mom! </p>
</a><h3 style="color: #edc256;"><a id="d0e747" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e751" xmlns:saxon="http://icl.com/saxon">CK SECRET </a></h3><a id="d0e751" xmlns:saxon="http://icl.com/saxon">
<p class="CKsecret" style="color: #edc256;">There are many wonderful online sources, like Zazzle or Moo,
                    that offer personalized stationery. If staying true to midcentury custom: Men,
                    order a single “correspondence card” (no fold) with your name embossed across
                    the top. Ladies, opt for a more decorative folding card, if you prefer. </p>
</a></div><a id="d0e751" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of handwritten note.">
<img alt="Photo of handwritten note." src="images/0095_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e3e2d2;"><a id="d0e751" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #00895b;">40</p>
</a><h2 style="color: #7e983b;"><a id="d0e751" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e763" xmlns:saxon="http://icl.com/saxon">White Out </a></h2><a id="d0e763" xmlns:saxon="http://icl.com/saxon">
<p>My sure-fire tip for bringing a modern touch to any kitchen? White countertops.
                    My preference is natural quartz from Silestone, but Corian or even Formica (that
                    midcentury marvel) will also do the trick.</p>
<p class="cap">To set yourself apart, avoid granite—the staple of tract homes and
                    big box builders. A white counter, even when paired with more traditional
                    cabinetry, will provide a breath of fresh air to your kitchen. </p>
</a></div><a id="d0e763" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of kitchen with white counters.">
<img alt="Photo of kitchen with white counters." src="images/0099_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of kitchen with white counters.">
<img alt="Photo of kitchen with white counters." src="images/page72.jpg"/>
</div>
<div class="mediaobject" title="Photo of kitchen with white counters.">
<img alt="Photo of kitchen with white counters." src="images/0097_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e9882a;"><a id="d0e763" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #f4b936;">41</p>
</a><h2><a id="d0e763" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e787" xmlns:saxon="http://icl.com/saxon">Black &amp; Light</a></h2><a id="d0e787" xmlns:saxon="http://icl.com/saxon">
<p>Believe it or not, I am a simple guy. I like things classic. I try not to get
                    tricky with building materials. Marble was a design staple thousands of years
                    ago—and fifty years ago. Who am I to mess with success? </p>
<p class="cap" style="color: #ffffff;">In this Palm Springs living room, black marble edged with brass trim
                    provides a dramatic backdrop for artwork from my client’s native South Africa,
                    paired with a sculpture purchased on a trip to Asia. This timeless material of
                    marble, which is also surprisingly affordable, lets my client’s collection take
                    center stage.</p>
</a></div><a id="d0e787" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of black and white fireplace.">
<img alt="Photo of black and white fireplace." src="images/0103_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #000000;"><a id="d0e787" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #b4a334;">42</p>
</a><h2 style="color: #c36382;"><a id="d0e787" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e801" xmlns:saxon="http://icl.com/saxon">Think Pink</a></h2><a id="d0e801" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Pink was arguably <span class="emphasis">
<em>the</em>
</span> color of the 1950s; no doubt you have seen an iconic midcentury pink
                    bathroom. It started with Dorothy Draper in the 1940s and continued with Mamie
                    Eisenhower, who wore a pink gown bejeweled with 2,000 rhinestones to Ike’s
                    inauguration in 1953. Pink being her favorite color, Mamie reportedly had the
                    entire White House private quarters decorated in the hue, so it is no surprise
                    that pink soared to popularity in decorating and fashion during the decade. </p>
</a></div><a id="d0e801" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bedroom with pink wall.">
<img alt="Photo of bedrrom with pink wall." src="images/0101_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #80c4d0;"><a id="d0e801" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #b5d2d7;">43</p>
</a><h2 style="color: #f3b25d;"><a id="d0e801" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e816" xmlns:saxon="http://icl.com/saxon">Fire It Up</a></h2><a id="d0e816" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Nothing says “home” like a hearth—be it inside or out. From space-age metal cones
                    to solid stone chimneys, most midcentury homes were centered around a
                    fireplace.</p>
<p class="cap">The present-day gas fireplaces, often covered with a thin piece of
                    glass, leave me a bit cold. A roaring fire takes me back: to campgrounds, to
                    holidays, to bedtime stories. Put your smart phone down, pull up a chair, raise
                    a glass, and gather ’round. You will be glad you did.</p>
</a></div><a id="d0e816" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of firepit.">
<img alt="Photo of firepit." src="images/0107a_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e4d85d;"><a id="d0e816" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #e16026;">44</p>
</a><h2 style="color: #8cb6bd;"><a id="d0e816" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e830" xmlns:saxon="http://icl.com/saxon">Eero Won’t Steer You
                    Wrong</a></h2><a id="d0e830" xmlns:saxon="http://icl.com/saxon">
<p>As an interior designer, I spend much of my time selecting furniture. Dining
                    tables, in general, are an item that I frequently agonize over. How many people
                    do my clients need to seat for dinner? Will the finish be durable? Is the scale
                    right for the room? </p>
<p>I really shouldn’t fret. Eero Saarinen (who also designed the St. Louis Gateway
                    Arch) created the perfect table several decades ago. </p>
<p>His marble table works in almost every setting. Place this table in a traditional
                    home, and like a fresh breeze on a warm day, it instantly lightens the air. The
                    table easily seats eight, which is almost always plenty. Done in marble, the
                    table is durable and timeless. Its oval shape allows for easy conversation
                    amongst all diners. Bravo, Mr. Saarinen! </p>
</a><h3 style="color: #e16026;"><a id="d0e830" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e838" xmlns:saxon="http://icl.com/saxon">CK SECRET </a></h3><a id="d0e838" xmlns:saxon="http://icl.com/saxon">
<p class="CKsecret" style="color: #e16026;">Don’t have the budget for the marble table? The fiberglass
                    version is equally versatile. Even Ikea makes a very good option that captures
                    the essence of Eero’s original intent.</p>
</a></div><a id="d0e838" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of white dining table.">
<img alt="Photo of white dining table." src="images/0112_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #f9f4e5;"><a id="d0e838" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #e8e2ad;">45</p>
</a><h2 style="color: #b48d32;"><a id="d0e838" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e850" xmlns:saxon="http://icl.com/saxon">Show Your Roots</a></h2><a id="d0e850" xmlns:saxon="http://icl.com/saxon">
<p>For all the clean lines and square corners of midcentury design, there was a
                    simultaneous movement to embrace nature. The great furniture designer George
                    Nakashima experimented with the juxtaposition of live edges and machine
                    precision. Each enhances the other. </p>
</a></div><a id="d0e850" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bedroom.">
<img alt="Photo of bedroom." src="images/0110_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #000000;"><a id="d0e850" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c6ba5a;">46</p>
</a><h2 style="color: #f0eddc;"><a id="d0e850" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e862" xmlns:saxon="http://icl.com/saxon">Rub-a-Dub-Tub</a></h2><a id="d0e862" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">When updating a bathroom, I favor sleek new fiberglass bathtubs. They save space
                    and, in my mind, are the direct descendants of Eames and Jacobsen fiberglass
                    furniture— with just a hint of Mork’s spaceship thrown in for good measure. </p>
</a></div><a id="d0e862" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of fiberglass bathtub.">
<img alt="Photo of fiberglass bathtub." src="images/0108_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #da353d;"><a id="d0e862" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #97a778;">47</p>
</a><h2><a id="d0e862" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e874" xmlns:saxon="http://icl.com/saxon">Black, White &amp; Red
                    All Over</a></h2><a id="d0e874" xmlns:saxon="http://icl.com/saxon">
<p>From Piet Mondrian to Charles and Rae Eames, iconic designers have known the
                    power of these three colors for nearly a century. While it is admittedly not my
                    favorite color, no other hue packs the punch of red. I turned to this classic
                    color scheme in the living room of the desert retreat I created for a Silicon
                    Valley mogul. </p>
</a></div><a id="d0e874" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0109_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0111_MMM.jpg"/>
<div class="caption">
<p>In a nod to my 1970s roots, I topped the room off with a photo mural
                        depicting Joshua Tree National Park.</p>
</div>
</div>
</a><div class="sidebar" style="background-color: #98792f;"><a id="d0e874" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c6ba5a;">48</p>
</a><h2 style="color: #40b8ba;"><a id="d0e874" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e894" xmlns:saxon="http://icl.com/saxon">Put Some Sizzle in Your
                    Swizzle</a></h2><a id="d0e894" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">My parents collected swizzle sticks from their travels and evenings out. I
                    inherited the tradition, but these supper club relics are becoming increasingly
                    less common. Sentimentalist that I am, I carry these beauties from California
                    Lustre at my shop in Palm Springs. More often, I use them for my coffee. Stir
                    away!</p>
</a></div><a id="d0e894" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of swizzle sticks.">
<img alt="Photo of swizzle sticks." src="images/0192_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #bfd06c;"><a id="d0e894" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #9098a3;">49</p>
</a><h2 style="color: #ffffff;"><a id="d0e894" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e906" xmlns:saxon="http://icl.com/saxon">Flower Power</a></h2><a id="d0e906" xmlns:saxon="http://icl.com/saxon">
<p style="color: #144b8b;">Bring some 1960s peace and love into your home with a groovy floral pattern.
                    Flowers are perennial symbols of fleeting beauty and the preciousness of life.
                    Classic Marimekko patterns are a good place to start. </p>
</a></div><a id="d0e906" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bedroom.">
<img alt="Photo of bedroom." src="images/0119_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #005f68;"><a id="d0e906" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #80c4d0;">50</p>
</a><h2 style="color: #ec9b2b;"><a id="d0e906" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e918" xmlns:saxon="http://icl.com/saxon">serve it up</a></h2><a id="d0e918" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Vintage melamine dishware is an affordable and simple way to add some midcentury
                    nostalgia to your entertaining. In addition, melamine is nearly indestructible
                    and comes in every color of the rainbow: Now serving fun!</p>
</a></div><a id="d0e918" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of vintage melamine dishware.">
<img alt="Photo of vintage melamine dishware." src="images/0201_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #80c4d0;"><a id="d0e918" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #f6d77b;">51</p>
</a><h2 style="color: #ffffff;"><a id="d0e918" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e930" xmlns:saxon="http://icl.com/saxon">Down to the Wire</a></h2><a id="d0e930" xmlns:saxon="http://icl.com/saxon">
<p style="color: #144b8b;">If you don’t live in sunshine year-round, midcentury metal furniture will
                    withstand the colder months. No need to find a complete set—it is more fun to
                    mix and match a variety of outdoor metal styles. A coat of white outdoor spray
                    paint, available off the shelf at your local hardware store, will bring it all
                    together. </p>
</a></div><a id="d0e930" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of outdoor metal furniture.">
<img alt="Photo of outdoor metal furniture." src="images/0122_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #a1873b;"><a id="d0e930" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #97a788;">52</p>
</a><h2 style="color: #faeec3;"><a id="d0e930" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e942" xmlns:saxon="http://icl.com/saxon">Wake Up</a></h2><a id="d0e942" xmlns:saxon="http://icl.com/saxon">
<p>Dating back to 1897, the Heywood-Wakefield Company is something of an unsung hero
                    in midcentury design. Their recognizable Art Deco–inspired pieces, often made
                    using steam to bend the wood and a bleaching process to lighten it, have become
                    collector’s items. </p>
</a></div><a id="d0e942" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of Art Deco-inspired lamp.">
<img alt="Photo of Art Deco-inspired lamp." src="images/0123_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #b9d243;"><a id="d0e942" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #fff100;">53</p>
</a><h2 style="color: #0b7571;"><a id="d0e942" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e954" xmlns:saxon="http://icl.com/saxon">Mix It Up</a></h2><a id="d0e954" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Interiors in the middle of the twentieth century didn’t just turn Danish modern
                    overnight. Unless you were part of the midcentury 1 percent, it is unlikely that
                    you suddenly transformed your home’s decor. Most Americans adopted the new trend
                    slowly, adding a piece here or there. Thus, true midcentury interiors were a mix
                    of more traditional pieces and the emerging modern style.</p>
<p class="cap" style="color: #144b8b;">Louis XIV armchairs mix with a Noguchi-inspired dining table and ’70s
                    mod fabric in my eclectic design for this dining room. </p>
</a></div><a id="d0e954" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of dining area.">
<img alt="Photo of dining area." src="images/0125_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #233023;"><a id="d0e954" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #97a788">54</p>
</a><h2 style="color: #b87a2a;"><a id="d0e954" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e968" xmlns:saxon="http://icl.com/saxon">Walnut and White</a></h2><a id="d0e968" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">. . . and everything bright! No other color combination captures the midcentury
                    essence as these two complementary hues. Like ebony and ivory, together they
                    create beautiful music. </p>
</a></div><a id="d0e968" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of fireplace.">
<img alt="Photo of fireplace." src="images/0124_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #8a7b6a;"><a id="d0e968" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #cac9ba;">55</p>
</a><h2 style="color: #a3ae52;"><a id="d0e968" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e980" xmlns:saxon="http://icl.com/saxon">Less Is More</a></h2><a id="d0e980" xmlns:saxon="http://icl.com/saxon">
<p>Pare it down. Let it go. It is just stuff. Most of us have far too many
                    possessions. What do you really need? What moves you? What inspires you? Focus
                    on that, and forget the rest. </p>
</a></div><a id="d0e980" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of fireplace.">
<img alt="Photo of fireplace." src="images/0126_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #c9872f;"><a id="d0e980" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #708d7f;">56</p>
</a><h2 style="color: #a53735;"><a id="d0e980" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e992" xmlns:saxon="http://icl.com/saxon">Block Party</a></h2><a id="d0e992" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">I have had a minor obsession with grids since my days in architecture school. It
                    is no wonder that my Franklin credenza draws inspiration from cubist geometry.
                    Throw in a bouffant hairdo and a dry martini, and you had me at “hello.” </p>
</a></div><a id="d0e992" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of modern art.">
<img alt="Photo of modern art." src="images/0127_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e4d169;"><a id="d0e992" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #a3ae52;">57</p>
</a><h2 style="color: #ffffff;"><a id="d0e992" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1004" xmlns:saxon="http://icl.com/saxon">Horse Around</a></h2><a id="d0e1004" xmlns:saxon="http://icl.com/saxon">
<p style="color: #666156;">Equine majesty has captured the human imagination for millennia. Grace, strength,
                    loyalty, dignity, hard work—we can learn much from these four-legged companions.
                    I paired a vintage ceramic horse sculpture with a 1960s faux bamboo lamp for a
                    tableau of striking simplicity.</p>
<p style="color: #ffffff;">Don’t be afraid of the pleated shade!</p>
</a></div><a id="d0e1004" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a lamp.">
<img alt="Photo of a lamp." src="images/0132_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #592917;"><a id="d0e1004" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #40251d;">58</p>
</a><h2 style="color: #c6ba5a;"><a id="d0e1004" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1018" xmlns:saxon="http://icl.com/saxon">Circle the Wagons</a></h2><a id="d0e1018" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Why play it straight? It is so expected. A semicircular sofa invites conversation
                    and sets a retro tone.</p>
<p class="cap" style="color: #c6ba5a;">BONUS ROUND: Cork floors are both eco-friendly and soft underfoot.
                </p>
</a></div><a id="d0e1018" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0129_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #67371f;"><a id="d0e1018" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #85a497;">59</p>
</a><h2 style="color: #437b85;"><a id="d0e1018" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1032" xmlns:saxon="http://icl.com/saxon">Take a Seat</a></h2><a id="d0e1032" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Interested in mid-mod design but don’t know where to start? Take a seat, my
                    friend. One good chair can transform a room. They say that a chair is the most
                    difficult piece of furniture to design and if done correctly captures the
                    essence of an entire design movement. </p>
</a></div><a id="d0e1032" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bedroom.">
<img alt="Photo of bedroom." src="images/0128_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #df7d2a;"><a id="d0e1032" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #117c3e;">60</p>
</a><h2 style="color: #932724;"><a id="d0e1032" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1044" xmlns:saxon="http://icl.com/saxon">Procure a platner</a></h2><a id="d0e1044" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Warren Platner (1919–2006) designed one of the most enduring dining sets in the
                    history of interior design. He also created the interiors for the Ford
                    Foundation and the Windows on the World restaurant atop the World Trade Center.
                    Used separately or together, these pieces stand the test of time. </p>
<p class="cap" style="color: #c6ba5a;">After receiving his degree in architecture from Cornell University in
                    1941, Platner worked for some of the most renowned architects of the twentieth
                    century, including I. M. Pei, Raymond Loewy, and Eero Saarinen before opening
                    his own firm in 1967.</p>
</a></div><a id="d0e1044" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of dining area.">
<img alt="Photo of dining area." src="images/0130_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #655944;"><a id="d0e1044" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #e16026;">61</p>
</a><h2 style="color: #eec256;"><a id="d0e1044" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1058" xmlns:saxon="http://icl.com/saxon">Get Stoned</a></h2><a id="d0e1058" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Think Frank Lloyd Wright’s Fallingwater and E. Stewart Williams’ Frank Sinatra
                    Estate—great midcentury houses were often built around a central stone
                    fireplace. Go for the real deal if you can, but today we have stone veneer to
                    create a <span class="bold">
<strong>trompe l’oeil</strong>
</span> masonry look. </p>
</a></div><a id="d0e1058" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of stone fireplace.">
<img alt="Photo of stone fireplace." src="images/0131_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #704e24;"><a id="d0e1058" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #dbcaba;">62</p>
</a><h2 style="color: #639ec9;"><a id="d0e1058" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1073" xmlns:saxon="http://icl.com/saxon">Flat Out Cool</a></h2><a id="d0e1073" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">With cabinetry, beauty is in the simplicity. No raised paneling for me, no thank
                    you! Forgo the adornment and let the beauty of the wood have its moment in the
                    sun. </p>
</a></div><a id="d0e1073" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of simple cabinetry.">
<img alt="Photo of simple cabinetry." src="images/0133_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #7f4923;"><a id="d0e1073" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c6ba5a;">63</p>
</a><h2 style="color: #ddd6c2;"><a id="d0e1073" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1085" xmlns:saxon="http://icl.com/saxon">Go Nuts</a></h2><a id="d0e1085" xmlns:saxon="http://icl.com/saxon">
<p style="color: #c6ba5a;">It is no secret that walnut is the preferred wood species of midcentury
                    designers. You want a midcentury look? Then get the red out. I tend to avoid any
                    wood finish that appears overly red or orange (with the notable exception of
                    teak). </p>
</a></div><a id="d0e1085" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of walnut table.">
<img alt="Photo of walnut table." src="images/0136_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #6b565d;"><a id="d0e1085" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #dcaf6e;">64</p>
</a><h2 style="color: #ffffff;"><a id="d0e1085" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1097" xmlns:saxon="http://icl.com/saxon">Butterfly Effect</a></h2><a id="d0e1097" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">The humble Butterfly Chair has a rich lineage: It was first designed in Buenos
                    Aires, Argentina, in 1938 by the architects Antonio Bonet, Juan Kurchan, and
                    Jorge Ferrari Hardoy, who met as assistants in Le Corbusier’s Paris studio.
                    (Yes, that Le Corbusier!) Their utilitarian seat inspired today’s camping
                    chairs, that modern staple of tailgate parties and campouts across the USA.
                    While it does not come with a built-in cup holder, I much prefer the original.
                    Simple, sleek, and affordable, it brings style and sophistication wherever it
                    goes. </p>
</a></div><a id="d0e1097" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of Butterly Chairs outdoors.">
<img alt="Photo of Butterly Chairs outdoors." src="images/0137_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #c5562a;"><a id="d0e1097" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #89a53e;">65</p>
</a><h2 style="color: #fbd355;"><a id="d0e1097" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1109" xmlns:saxon="http://icl.com/saxon">wipe your mouth</a></h2><a id="d0e1109" xmlns:saxon="http://icl.com/saxon">
<p>Bring back a bit of midcentury propriety and sophistication with cloth napkins.
                    Vintage collectible linen sets are very affordable and easy to find on the
                    Internet or at flea markets. No need to save them for special occasions—if you
                    can wash your undies, you can wash napkins. They elevate any meal into an event,
                    and the landfill will thank you. As that immortal advertising adage says,
                    “You’re worth it.” </p>
</a></div><a id="d0e1109" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of cloth napkins.">
<img alt="Photo of cloth napkins." src="images/0191_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #9c9f99;"><a id="d0e1109" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #708d7f;">66</p>
</a><h2 style="color: #666156;"><a id="d0e1109" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1121" xmlns:saxon="http://icl.com/saxon">Cut the Grass</a></h2><a id="d0e1121" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Afraid of wallpaper? Fear that modern design is too sterile for you? Take the
                    edge off with wallpaper made from bamboo, jute, rush, or sea grass. A staple of
                    midcentury interior design, grasscloth has stood the test of time. </p>
</a></div><a id="d0e1121" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of grasscloth wallpaper.">
<img alt="Photo of grasscloth wallpaper." src="images/0047_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of bedroom.">
<img alt="Photo of bedroom." src="images/page125.jpg"/>
</div>
<div class="mediaobject" title="Photo of bedroom.">
<img alt="Photo of bedroom." src="images/0046_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #a7bf38;"><a id="d0e1121" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #e1f77e;">67</p>
</a><h2 style="color: #b87a2a;"><a id="d0e1121" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1143" xmlns:saxon="http://icl.com/saxon">Set the Table</a></h2><a id="d0e1143" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">There was a time when families sat down to dinner together; when people would
                    talk, not text; when anniversaries and birthday messages were not the domain of
                    social media. Turn the television off and put down the smart phone. Light the
                    candles, break the bread, raise your glass. Life is a series of golden moments,
                    every day and in every way. So set the table; get out the good china. Why not
                    tonight? </p>
</a></div><a id="d0e1143" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a set table.">
<img alt="Photo of a set table." src="images/0197_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #b4c2c7;"><a id="d0e1143" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ccb92f;">68</p>
</a><h2 style="color: #ffffff;"><a id="d0e1143" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1155" xmlns:saxon="http://icl.com/saxon">Fresh Coat</a></h2><a id="d0e1155" xmlns:saxon="http://icl.com/saxon">
<p>A simple and surefire way to give your home a modern face-lift is with a coat of
                    white paint. White was the go-to color of modernist architects, starting in the
                    1920s with the Bauhaus and International styles. White offers purity of
                    expression in architectural design while enhancing the play of light and shadow.
                </p>
</a></div><a id="d0e1155" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a set table.">
<img alt="Photo of a set table." src="images/0180_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #41a6b2;"><a id="d0e1155" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">69</p>
</a><h2 style="color: #b74829;"><a id="d0e1155" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1167" xmlns:saxon="http://icl.com/saxon"> “Terr” it Up</a></h2><a id="d0e1167" xmlns:saxon="http://icl.com/saxon">
<p>Originally invented by the Venetians (so you know it’s good), terrazzo flooring
                    is poured in place and composed of scraps of quartz, marble, granite, and/or
                    glass held together with a polymer resin. Many of us grew up with terrazzo
                    floors in our elementary schools and department stores, so the material
                    continues to have nostalgic appeal. Why not use terrazzo technology in a modern
                    way, on your kitchen counters? Composed of 100 percent post-consumer recycled
                    glass, these slabs from Vetrazzo are both beautiful and eco-friendly. </p>
</a></div><a id="d0e1167" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of kitchen.">
<img alt="Photo of kitchen." src="images/0174_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #8ea63b;"><a id="d0e1167" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #e07031;">70 </p>
</a><h2 style="color: #80c4d0;"><a id="d0e1167" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1179" xmlns:saxon="http://icl.com/saxon">Dine In</a></h2><a id="d0e1179" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">From Arnold’s on <span class="emphasis">
<em>Happy Days</em>
</span> to Mel’s Diner on <span class="emphasis">
<em>Alice</em>
</span> and The Peach Pit on <span class="emphasis">
<em>90210,</em>
</span> many life moments have been immortalized at a small-screen diner. Set
                    the stage for your life by turning a cramped nook into a diner-inspired eating
                    space with banquette seating. </p>
<p class="cap">Your booth seating does not need to be custom. A local discount home
                    store will carry leather benches; push them against the wall and pile on some
                    throw pillows to create your version of the classic American diner. Ding,
                    ding—order’s up! </p>
</a></div><a id="d0e1179" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of dining area.">
<img alt="Photo of dining area." src="images/0177_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #0a6e86;"><a id="d0e1179" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #b87a2a;">71</p>
</a><h2 style="color: #ffffff;"><a id="d0e1179" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1202" xmlns:saxon="http://icl.com/saxon">take the plunge</a></h2><a id="d0e1202" xmlns:saxon="http://icl.com/saxon">
<p>If you love something, go for it. Dive in, feet first. Especially when it comes
                    to your interior. If you love something the first time you see it, chances are
                    you will love it the thousandth time. The great midcentury designers were not
                    timid; they were brave. They put themselves out there. They took risks—and it
                    paid off. </p>
<p class="cap" style="color: #ffffff;">In this small powder room, I embraced the dark space and my client’s
                    love of the sea to create a magical and immersive experience.</p>
</a></div><a id="d0e1202" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bathroom.">
<img alt="Photo of bathroom." src="images/0178_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #dcdbd3;"><a id="d0e1202" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #0e9ea8;">72</p>
</a><h2><a id="d0e1202" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1216" xmlns:saxon="http://icl.com/saxon">Shag, Baby!</a></h2><a id="d0e1216" xmlns:saxon="http://icl.com/saxon">
<p>Technically dating from the 1970s (much like myself), shag carpeting has
                    nonetheless become synonymous with today’s midcentury-inspired interior. A shag
                    rug, be it synthetic or the more natural flokati style, is my preferred floor
                    covering to unify an eclectic interior. </p>
</a></div><a id="d0e1216" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of living area with shag carpet.">
<img alt="Photo of living area with shag carpet." src="images/0182_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #8ba1b0;"><a id="d0e1216" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #26525a;">73</p>
</a><h2 style="color: #eceadc;"><a id="d0e1216" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1228" xmlns:saxon="http://icl.com/saxon">Be Brutally Honest</a></h2><a id="d0e1228" xmlns:saxon="http://icl.com/saxon">
<p>A descendant of International Style modernism, Brutalism in architecture dates
                    from the 1950s to the 1970s. Brutalist décor elements favored raw-edge metal or
                    concrete and reached their heyday in the 1970s. Think <span class="emphasis">
<em>American Hustle, </em>
</span>not <span class="emphasis">
<em>Grease</em>
</span>, and you will get the idea. Warning, dear modernist: as with many good
                    things, a little can go a long way.</p>
</a></div><a id="d0e1228" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of metal lamp.">
<img alt="Photo of metal lamp." src="images/0184_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of bedroom.">
<img alt="Photo of bedroom." src="images/0183_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #5b9c42;"><a id="d0e1228" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #cdd22f;">74</p>
</a><h2 style="color: #40b8ba;"><a id="d0e1228" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1251" xmlns:saxon="http://icl.com/saxon">Ask Trina</a></h2><a id="d0e1251" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">There may be no singular, simpler gesture to bringing midcentury joie de vivre
                    into your space than accenting with Trina Turk pillows. The queen of retro sunny
                    California fashion, just one pillow from Trina’s home goods line will brighten
                    your day. In multiples, you may need to wear shades. </p>
</a></div><a id="d0e1251" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of pillows used outdoors.">
<img alt="Photo of pillows used outdoors." src="images/0186_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #4c514a;"><a id="d0e1251" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c26c2c;">75</p>
</a><h2 style="color: #40b8ba;"><a id="d0e1251" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1263" xmlns:saxon="http://icl.com/saxon">Buy a Bar Cart</a></h2><a id="d0e1263" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">No built-in wet bar? No problem! A staple of midcentury décor, a bar cart can
                    turn any unused corner into happy hour. You will practically be able to hear
                    Dean Martin crooning on the hi-fi.</p>
</a></div><a id="d0e1263" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of a bar cart.">
<img alt="Photo of a bar cart." src="images/0193_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #079379;"><a id="d0e1263" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #97a788;">76</p>
</a><h2 style="color: #ffffff;"><a id="d0e1263" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1275" xmlns:saxon="http://icl.com/saxon">Box it Up</a></h2><a id="d0e1275" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">From Cubism in art to Eames-era modular furniture, simple cubby shelving has a
                    proud lineage. No need to relegate them to children’s rooms and IKEA warehouses!
                    These geometric goodies will give you a place for everything and keep everything
                    in its place, while adding architectural rigor to your décor.</p>
</a></div><a id="d0e1275" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of shelving unit.">
<img alt="Photo of shelving unit." src="images/0187_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #b74829;"><a id="d0e1275" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #b87a2a;">77</p>
</a><h2 style="color: #fbd355;"><a id="d0e1275" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1287" xmlns:saxon="http://icl.com/saxon">Bottoms Up!</a></h2><a id="d0e1287" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">In Palm Springs, we have our cocktail culture down to a science. No matter where
                    you live, add some swank to your mixology with a set of vintage glassware. </p>
</a></div><a id="d0e1287" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of vintage glasses.">
<img alt="Photo of vintage glasses." src="images/0190_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #666159;"><a id="d0e1287" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #b87a2a;">78</p>
</a><h2 style="color: #ffffff;"><a id="d0e1287" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1299" xmlns:saxon="http://icl.com/saxon">Express Yourself</a></h2><a id="d0e1299" xmlns:saxon="http://icl.com/saxon">
<p>Midcentury stoicism is one nostalgic principle to which I do not ascribe. Let it
                    out: laugh, cry, scream, sing. Emotions are the voice of the soul. Let it be
                    heard and let your home reflect who you are. </p>
<p class="cap" style="color: #c6ba5a;">Do you ever feel like the woman in Nina Fowler’s <span class="emphasis">
<em>Wild Ride? </em>
</span>We all do, sometimes! Let it out. It is the best way to move on. </p>
</a></div><a id="d0e1299" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0198_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #edc256;"><a id="d0e1299" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">79</p>
</a><h2><a id="d0e1299" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1316" xmlns:saxon="http://icl.com/saxon">Top Shelf</a></h2><a id="d0e1316" xmlns:saxon="http://icl.com/saxon">
<p>Midcentury designers were masters of form and function, finding solutions that
                    were elegant in their simplicity. I increased the function of this compact
                    kitchen by adding three floating shelves to the existing cabinetry design. They
                    keep everyday dishware at arm’s reach while injecting color into the space; the
                    black faux crocodile wallpaper adds a dose of depth and drama. </p>
</a></div><a id="d0e1316" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of kitchen.">
<img alt="Photo of kitchen." src="images/0199_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #c31772;"><a id="d0e1316" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c6ba5a;">80</p>
</a><h2 style="color: #493367;"><a id="d0e1316" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1328" xmlns:saxon="http://icl.com/saxon">Atom Bomb</a></h2><a id="d0e1328" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">From cellular biology to outer space, great minds of the mid-twentieth century
                    began to explore every part of our incredible world. Bring a bit of that
                    scientific curiosity home with molecule-inspired furniture and an
                    out-of-this-world color palette. </p>
</a></div><a id="d0e1328" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of glass and brass table.">
<img alt="Photo of glass and brass table." src="images/0206_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0159_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #4c514a;"><a id="d0e1328" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #b99753;">81</p>
</a><h2 style="color: #95a098;"><a id="d0e1328" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1345" xmlns:saxon="http://icl.com/saxon">I can’t do that, Dave</a></h2><a id="d0e1345" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Few household items are decidedly less midcentury than our (wonderful) big-screen
                    televisions. These modern marvels are more “HAL 9000” than 1968. Midcentury
                    televisions were often incorporated into pleasing pieces of casegoods. In that
                    tradition, I try to camouflage flat-screens by placing them against a dark wall
                    (wood paneling, paint, or wallpaper are all good options), minimizing their
                    looming presence. Bonus: the deep background color is easier on the eyes when
                    viewing your favorite show. </p>
</a></div><a id="d0e1345" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of flat screen TV.">
<img alt="Photo of flat screen TV." src="images/0160_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #7d4665;"><a id="d0e1345" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #681737;">82</p>
</a><h2 style="color: #40b8ba;"><a id="d0e1345" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1357" xmlns:saxon="http://icl.com/saxon">Grandma Knows Best</a></h2><a id="d0e1357" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Floral patterns were really quite common in 1950s and 1960s interiors but are
                    often left out of the textbooks and overshadowed by innovations of the era. For
                    those who worry that modernism might be too masculine for your taste, you’re in
                    luck: “Granny florals” are making a comeback. </p>
</a></div><a id="d0e1357" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of floral wallpaper.">
<img alt="Photo of floral wallpaper." src="images/0166_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of room with floral wallpaper.">
<img alt="Photo of room with floral wallpaper." src="images/0165_MMM.jpg"/>
<div class="caption">
<p>I tempered the bold geometry of this bedroom with an oversize floral print
                        from Designers Guild.</p>
</div>
</div>
</a><div class="sidebar" style="background-color: #889a9d;"><a id="d0e1357" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #fbd355;">83</p>
</a><h2 style="color: #313836;"><a id="d0e1357" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1377" xmlns:saxon="http://icl.com/saxon">mixed messages</a></h2><a id="d0e1377" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">One of the design questions I am asked most often is, “Can I mix metals in my
                    home?” My answer is a resounding, “Yes!” Most of my clients opt for chrome or
                    nickel fixtures but don’t hesitate to mix in brass or gold accents, like this
                    vintage mirror in an otherwise modern bathroom. </p>
</a></div><a id="d0e1377" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bathroom.">
<img alt="Photo of bathroom." src="images/0167_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e3a6b2;"><a id="d0e1377" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #de528f;">84</p>
</a><h2 style="color: #ffffff;"><a id="d0e1377" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1389" xmlns:saxon="http://icl.com/saxon">Be a Barbie Girl</a></h2><a id="d0e1389" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Launched in 1959 by Mattel, Barbie<sup>®</sup> is a true midcentury icon. But
                    don’t let your beauty be only skin deep, my modern friend. True allure requires
                    both style and substance. </p>
</a></div><a id="d0e1389" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of Barbie themed bedroom.">
<img alt="Photo of Barbie themed bedroom." src="images/0161_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #b42f41;"><a id="d0e1389" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #e4d148;">85</p>
</a><h2 style="color: #40b8ba;"><a id="d0e1389" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1404" xmlns:saxon="http://icl.com/saxon">Hang Out</a></h2><a id="d0e1404" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Defy gravity—and convention—by spending some time in a mod 1960s Bubble Chair,
                    originally designed by Eero Aarnio of Finland. If you prefer your décor more
                    organic, you can opt for the Bohemian chic rattan version. Who says your feet
                    need to be planted on the ground? </p>
</a></div><a id="d0e1404" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of Bubble Chair.">
<img alt="Photo of Bubble Chair." src="images/0163_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #41a2a9;"><a id="d0e1404" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #80c4d0;">86</p>
</a><h2 style="color: #0a4683;"><a id="d0e1404" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1416" xmlns:saxon="http://icl.com/saxon">Bunny Hop</a></h2><a id="d0e1416" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Think cute has no place in modernism? Think again! Reinvent the classic English
                    animal painting with bold lines and bright colors, as artist Hunt Slonem has
                    done with his rabbit portraits. Above all, never take your home (or yourself)
                    too seriously. </p>
</a></div><a id="d0e1416" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of room with rabbit pictures.">
<img alt="Photo of room with rabbit pictures." src="images/0164_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #bdcfcf;"><a id="d0e1416" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #5ca3be;">87</p>
</a><h2 style="color: #c1ac3e;"><a id="d0e1416" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1428" xmlns:saxon="http://icl.com/saxon">Love is All You Need</a></h2><a id="d0e1428" xmlns:saxon="http://icl.com/saxon">
<p>For all the advancements of the past several decades, we often feel more
                    fractured than ever. For all our connectivity, it is easy to feel alone. Go
                    forth with love in your heart. Donate to that charity you admire; accept that
                    last-minute invitation; serve on that committee. Adopt a shelter animal—a pet
                    will teach you the meaning of love, to be sure. Tell your family you love them,
                    for life is precious and fleeting. Alone, we may not be able to change the
                    world, but we can change our corner of it. We do so by spreading love, little by
                    little, day by day. Choose love. Today. </p>
<p class="cap">For this love-filled den, I painted the walls a deep high-gloss teal
                    for richness, complemented by pops of bright white and jet black, all accented
                    with gold for a bit of shine.</p>
</a></div><a id="d0e1428" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of dressing table.">
<img alt="Photo of dressing table." src="images/0168_MMM.jpg"/>
</div>
<div class="mediaobject" title="Photo of living area.">
<img alt="Photo of living area." src="images/0169_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #306532;"><a id="d0e1428" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #d5b62d;">88</p>
</a><h2 style="color: #80c4d0;"><a id="d0e1428" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1447" xmlns:saxon="http://icl.com/saxon">Be Optimistic</a></h2><a id="d0e1447" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">After World War II, America experienced a natural high. We built suburbs; we had
                    babies; we created gadgets. A few years later, John F. Kennedy announced that we
                    would put a man on the moon. In short, we lived in a world of possibility. Our
                    homes reflected that sense of unbridled optimism. </p>
<p style="color: #ffffff;">Come on, America, let’s dream again! As Eleanor Roosevelt said, “The future
                    belongs to those who believe in the beauty of their dreams.” </p>
<p style="color: #ffffff;">I, for one, cannot wait to see what’s next. </p>
</a></div><a id="d0e1447" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of anything could happen card.">
<img alt="Photo of anything could happen card." src="images/0207_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #8b9288;"><a id="d0e1447" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #edc256;">89 </p>
</a><h2 style="color: #ffffff;"><a id="d0e1447" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1463" xmlns:saxon="http://icl.com/saxon">Steel Yourself</a></h2><a id="d0e1463" xmlns:saxon="http://icl.com/saxon">
<p>You don’t need to have barbarians at the gate to desire a formidable first
                    impression. Many of the homes built for celebrities during the Palm Springs
                    midcentury heyday featured an imposing front wall with few or no windows.
                    Privacy aside, a little mystery goes a long way.</p>
</a></div><a id="d0e1463" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of exterior of home.">
<img alt="Photo of exterior of home." src="images/0212_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #d6e1e8;"><a id="d0e1463" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #636363;">90</p>
</a><h2 style="color: #40b8ba;"><a id="d0e1463" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1475" xmlns:saxon="http://icl.com/saxon">Tread Lightly</a></h2><a id="d0e1475" xmlns:saxon="http://icl.com/saxon">
<p>While a tradition that is more Canadian and Asian — removing your shoes when
                    entering a home — has a ritualistic appeal and documented health benefits. I
                    created an unexpected entry moment with a vintage shelving unit from the Pierre
                    Cardin retail stores and a simple basket from Pottery Barn. </p>
</a></div><a id="d0e1475" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of entryway.">
<img alt="Photo of entryway." src="images/0144_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #00a193;"><a id="d0e1475" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #82bdcb;">91 </p>
</a><h2><a id="d0e1475" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1487" xmlns:saxon="http://icl.com/saxon">BOTTLE LOGIC </a></h2><a id="d0e1487" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">I have never been accused of over-accessorizing. That said, I do believe that
                    “it’s the small things,” and that details give a room its soul. </p>
<p style="color: #ffffff;">So where to begin? Do you rush out to your nearest discount store and buy a bunch
                    of cheap junk? I say no. Don’t contribute to our disposable society. </p>
<p style="color: #ffffff;">Vintage bottles are one of my favorite accessories. They are easy to source
                    online, at antique stores, or estate sales. They can be used alone, but in
                    multiples they create a major statement. Try grouping numerous bottles of
                    various shapes in your preferred accent color to add instant retro flair and a
                    foolproof focal point.</p>
</a></div><a id="d0e1487" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bottle collection.">
<img alt="Photo of bottle collection." src="images/0138_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #b4c9d1;"><a id="d0e1487" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ffffff;">92</p>
</a><h2 style="color: #14468b;"><a id="d0e1487" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1503" xmlns:saxon="http://icl.com/saxon">Sheer Genius</a></h2><a id="d0e1503" xmlns:saxon="http://icl.com/saxon">
<p>Many modern homeowners are hesitant to install draperies, and yet, fabric window
                    treatments were standard fare in the 1950s and 1960s. I like to treat large
                    windows with wall-to-wall, floor-to-ceiling draperies. They add a layer of
                    warmth to a minimal interior. Draw them open to enjoy the view, or close them
                    for a layer of privacy and softness. </p>
</a></div><a id="d0e1503" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of sheer draperies.">
<img alt="Photo of sheer draperies." src="images/0145_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #095690;"><a id="d0e1503" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #0f4077;">93</p>
</a><h2 style="color: #40b8ba;"><a id="d0e1503" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1515" xmlns:saxon="http://icl.com/saxon">Clear Out</a></h2><a id="d0e1515" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Originally created in the 1930s by DuPont under the brand name Lucite, acrylic
                    resin was quickly adopted for a variety of decorative uses, including handbags,
                    jewelry, and furniture. In World War II, acrylic was widely used for military
                    applications including submarine periscopes and airplane windshields. After the
                    war, acrylic manufacturers needed to find commercial uses for the material, and
                    in the following decades, Lucite furniture had its heyday. </p>
</a></div><a id="d0e1515" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of Lucite table.">
<img alt="Photo of Lucite table." src="images/0158_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #795a2d;"><a id="d0e1515" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #c6ba5a;">94</p>
</a><h2><a id="d0e1515" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1527" xmlns:saxon="http://icl.com/saxon">Make Do</a></h2><a id="d0e1527" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Don’t have high ceilings? Don’t worry. Our modernist forebearers never hesitated
                    to swag a chandelier when necessary. When my client did not have tall ceilings
                    to hang the chandelier she loved, I chose to place it in an otherwise unused
                    corner and allow it to dangle dangerously low to the ground. We created an
                    instant focal point from both inside and out. Midcentury designers were
                    continual problem-solvers; be part of that tradition. </p>
</a></div><a id="d0e1527" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of large chandelier.">
<img alt="Photo of large chandelier." src="images/0142_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #8cd0da;"><a id="d0e1527" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #8ec355;">95</p>
</a><h2 style="color: #ffffff;"><a id="d0e1527" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1539" xmlns:saxon="http://icl.com/saxon">catch the breeze</a></h2><a id="d0e1539" xmlns:saxon="http://icl.com/saxon">
<p>When I want to add some midcentury modern verve to an unremarkable structure, I
                    go back to the building blocks. More specifically, to breeze blocks. Originally
                    designed to shelter a building envelope from the sun, these midcentury
                    workhorses were ubiquitous well into the 1970s (even gracing my college
                    dormitory) before falling out of favor. Now they are back and better than
                    ever.</p>
<p class="cap" style="color: #ffffff;">I added a breeze block wall to this project in Palm Desert, creating
                    privacy for a bedroom patio and a dramatic backdrop for outdoor dining.</p>
</a></div><a id="d0e1539" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of breeze block wall.">
<img alt="Photo of breeze block wall." src="images/0140_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #c9cc30;"><a id="d0e1539" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #ccb92f;">96</p>
</a><h2 style="color: #40b8ba;"><a id="d0e1539" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1553" xmlns:saxon="http://icl.com/saxon">Grow Your Garden</a></h2><a id="d0e1553" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">White picket fences. A patch of dirt to call your own. It is all part of the
                    American Dream. Nourish your inner homestead tendencies by planting something in
                    the ground. Herbs or vegetables are especially useful. Even if you live in an
                    apartment, a potted garden brings joy and connection to the earth. Plant.
                    Nurture. Watch it grow. </p>
</a></div><a id="d0e1553" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of desert garden.">
<img alt="Photo of desert garden." src="images/0143_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #4e5153;"><a id="d0e1553" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #9facb5;">97</p>
</a><h2 style="color: #d89072;"><a id="d0e1553" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1565" xmlns:saxon="http://icl.com/saxon">Buckle Up</a></h2><a id="d0e1565" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">We honor the past by looking to the future. If the great midcentury creators were
                    alive today, they would not be resting on their laurels. They would not be
                    imitating. They would be pushing design forward. Much like these dining chairs
                    do. Designed by my friends at The Phillips Collection, they take the classic
                    Verner Panton shape and reimagine it with—that’s right—recycled seat belts. Hold
                    onto your hats, modernists: the future looks bright.</p>
</a></div><a id="d0e1565" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of seatbelt chairs.">
<img alt="Photo of seatbelt chairs." src="images/0157_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #5b5062;"><a id="d0e1565" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #a6967e;">98</p>
</a><h2 style="color: #fef3db;"><a id="d0e1565" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1577" xmlns:saxon="http://icl.com/saxon">Find Meaning</a></h2><a id="d0e1577" xmlns:saxon="http://icl.com/saxon">
<p style="color: #ffffff;">Part of modernism, for me, is surrounding oneself with fewer things, but things
                    that have meaning. Proudly display your collections, even in the most unlikely
                    of places. Wear your heart on your sleeve. Show the world who and what you
                    are—or what you strive to be. </p>
</a></div><a id="d0e1577" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of religious figures.">
<img alt="Photo of religious figures." src="images/0139_MMM.jpg"/>
</div>
</a><div class="sidebar" style="background-color: #e8e5df;"><a id="d0e1577" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #7b4c4f;">99</p>
</a><h2 style="color: #a1873b;"><a id="d0e1577" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1589" xmlns:saxon="http://icl.com/saxon">Sleep Softly</a></h2><a id="d0e1589" xmlns:saxon="http://icl.com/saxon">
<p>It is relatively easy to find midcentury bedroom sets online, at your local flea
                    market, or at a vintage store. Though I rarely want a matched set, the pieces
                    are normally sold together and are even less expensive when bought this way. </p>
<p class="cap">I will incorporate the nightstands and dresser into my bedroom
                    designs (sometimes in different rooms), but the vintage beds are inevitably too
                    small for our modern mattresses and sleeping habits. This is why so many of my
                    bedroom designs incorporate fabric beds and headboards—they are easy to make and
                    then there is no worry about matching the vintage wood finish, and really, isn’t
                    it so much cozier? And much less matchy-matchy.</p>
</a></div><a id="d0e1589" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of bedroom.">
<img alt="Photo of bedroom." src="images/0202_MMM.jpg"/>
<div class="caption"><p>Photography © Grey Crawford.</p></div>
</div>
</a><div class="sidebar" style="background-color: #1f9db4;"><a id="d0e1589" xmlns:saxon="http://icl.com/saxon">
<p class="number" style="color: #40b8ba;">100</p>
</a><h2 style="color: #ffffff;"><a id="d0e1589" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e1603" xmlns:saxon="http://icl.com/saxon">Peace Out</a></h2><a id="d0e1603" xmlns:saxon="http://icl.com/saxon">
<p>For me, design is about joy. It is about creating places and products that put a
                    smile on your face. It is about a bedroom that inspires you when you open your
                    eyes; a living room that embraces you after a long day. It is about a respite
                    from the busyness of the world. If I have put a smile on your face, I consider
                    it a job well done. </p>
</a></div><a id="d0e1603" xmlns:saxon="http://icl.com/saxon">
<div class="mediaobject" title="Photo of interior of home.">
<img alt="Photo of interior of home." src="images/0141_MMM.jpg"/>
</div>
</a></div><a id="d0e1603" xmlns:saxon="http://icl.com/saxon">
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch02.vue?69db4604